import * as ex from 'excalibur';
import { Allegiance, ControlType } from '../types/enums';
import { GridPosition } from '../types/interfaces';
import { GRID_WIDTH, GRID_HEIGHT } from '../config/constants';
import { Actor } from '../core/actor';
import { Unit } from '../core/unit';
import { Grid } from '../core/grid';
import { UnitFactory } from './unitFactory';

// Class to generate random encounters
export class EncounterGenerator {
  private grid: Grid;
  private game: any; // Temporarily using 'any' until we define the Game class

  constructor(grid: Grid, game: any) {
    this.grid = grid;
    this.game = game;
  }

  // Generate a random encounter with units for both allegiances
  public generateRandomEncounter(): Actor[] {
    const allUnits: Actor[] = [];

    // Select a random formation for this encounter
    const formationIndex = Math.floor(Math.random() * 4);
    const formationNames = ["Classic Left vs Right", "Top vs Bottom", "Center vs Surround", "Diagonal"];
    console.log(`%c===== GENERATING RANDOM ENCOUNTER: ${formationNames[formationIndex]} =====`, 'font-weight: bold; font-size: 14px; color: green; background: rgba(0, 128, 0, 0.1); padding: 5px;');

    // Generate units for each allegiance
    const playerUnits = this.generateUnitsForAllegiance(Allegiance.Player);
    const enemyUnits = this.generateUnitsForAllegiance(Allegiance.Enemy);

    // Log player units created with emojis and colored background
    console.log(`%c${playerUnits.length} Player units created:`, 'font-weight: bold; color: blue; background: rgba(0, 0, 255, 0.1); padding: 3px;');
    playerUnits.forEach(unit => {
      const u = unit as Unit;
      console.log(`%c ${u.emoji} ${u.name} at (${u.gridX},${u.gridY}), HP: ${u.health}/${u.maxHealth}, Initiative: ${u.initiative}`,
        'background: rgba(0, 0, 255, 0.2); border-radius: 3px; padding: 2px 4px;');
    });

    // Log enemy units created with emojis and colored background
    console.log(`%c${enemyUnits.length} Enemy units created:`, 'font-weight: bold; color: red; background: rgba(255, 0, 0, 0.1); padding: 3px;');
    enemyUnits.forEach(unit => {
      const u = unit as Unit;
      console.log(`%c ${u.emoji} ${u.name} at (${u.gridX},${u.gridY}), HP: ${u.health}/${u.maxHealth}, Initiative: ${u.initiative}`,
        'background: rgba(255, 0, 0, 0.2); border-radius: 3px; padding: 2px 4px;');
    });

    allUnits.push(...playerUnits);
    allUnits.push(...enemyUnits);

    return allUnits;
  }

  private generateUnitsForAllegiance(allegiance: Allegiance): Actor[] {
    const units: Actor[] = [];
    const unitCount = this.getRandomUnitCount(allegiance);
    const spawnPositions = this.getSpawnPositionsForAllegiance(allegiance, unitCount);

    for (let i = 0; i < unitCount; i++) {
      // Get a random unit type
      const unitType = this.getRandomUnitType();

      // Skip if no valid spawn position
      if (!spawnPositions[i]) continue;

      // All units are automatic for "watch it go" mode
      const controlType = ControlType.Automatic;

      // Create initiative value (slightly biased toward player)
      const baseInitiative = 5;
      const initiativeBonus = allegiance === Allegiance.Player ? 2 : 0;
      const initiative = baseInitiative + initiativeBonus + Math.floor(Math.random() * 6);

      // Create unit
      const unit = UnitFactory.createUnit(
        unitType,
        spawnPositions[i].x,
        spawnPositions[i].y,
        allegiance,
        initiative,
        this.grid,
        controlType === ControlType.Manual
      );

      // Override control type
      (unit as Unit).controlType = controlType;

      // Add unit to grid
      this.grid.addActor(unit, spawnPositions[i].x, spawnPositions[i].y);
      this.game.add(unit);
      units.push(unit);
    }

    return units;
  }

  private getRandomUnitType(): string {
    const unitTypes = ['merk', 'imp', 'rogue', 'dog'];
    return unitTypes[Math.floor(Math.random() * unitTypes.length)];
  }

  private getRandomUnitCount(allegiance: Allegiance): number {
    // Increase unit counts for more chaotic battles
    // Player gets 4-6 units, enemy gets 5-8 units
    return allegiance === Allegiance.Player ?
      Math.floor(Math.random() * 3) + 4 :
      Math.floor(Math.random() * 4) + 5;
  }

  private getSpawnPositionsForAllegiance(allegiance: Allegiance, count: number): GridPosition[] {
    const positions: GridPosition[] = [];
    const area = this.getSpawnAreaForAllegiance(allegiance);

    // Try to find distinct valid positions
    let attempts = 0;
    while (positions.length < count && attempts < 50) {
      const x = Math.floor(area.x + Math.random() * area.width);
      const y = Math.floor(area.y + Math.random() * area.height);

      // Check if position is valid
      if (this.grid.isValidMove(x, y)) {
        // Check if not already used
        if (!positions.some(pos => pos.x === x && pos.y === y)) {
          positions.push({ x, y });
        }
      }
      attempts++;
    }

    return positions;
  }

  private getSpawnAreaForAllegiance(allegiance: Allegiance): { x: number, y: number, width: number, height: number } {
    // Different formation types
    const formationTypes = [
      // 0: Classic Left vs Right
      {
        [Allegiance.Player]: { x: 1, y: 1, width: Math.floor(GRID_WIDTH / 3), height: GRID_HEIGHT - 2 },
        [Allegiance.Enemy]: { x: GRID_WIDTH - Math.floor(GRID_WIDTH / 3) - 1, y: 1, width: Math.floor(GRID_WIDTH / 3), height: GRID_HEIGHT - 2 }
      },
      // 1: Top vs Bottom
      {
        [Allegiance.Player]: { x: 1, y: 1, width: GRID_WIDTH - 2, height: Math.floor(GRID_HEIGHT / 3) },
        [Allegiance.Enemy]: { x: 1, y: GRID_HEIGHT - Math.floor(GRID_HEIGHT / 3) - 1, width: GRID_WIDTH - 2, height: Math.floor(GRID_HEIGHT / 3) }
      },
      // 2: Center vs Surround
      {
        [Allegiance.Player]: { x: Math.floor(GRID_WIDTH / 3), y: Math.floor(GRID_HEIGHT / 3), width: Math.floor(GRID_WIDTH / 3), height: Math.floor(GRID_HEIGHT / 3) },
        [Allegiance.Enemy]: { x: 1, y: 1, width: GRID_WIDTH - 2, height: GRID_HEIGHT - 2 }
      },
      // 3: Diagonal
      {
        [Allegiance.Player]: { x: 1, y: 1, width: Math.floor(GRID_WIDTH / 2), height: Math.floor(GRID_HEIGHT / 2) },
        [Allegiance.Enemy]: { x: Math.floor(GRID_WIDTH / 2), y: Math.floor(GRID_HEIGHT / 2), width: Math.floor(GRID_WIDTH / 2) - 1, height: Math.floor(GRID_HEIGHT / 2) - 1 }
      }
    ];

    // Pick a random formation type
    const formationIndex = Math.floor(Math.random() * formationTypes.length);
    return formationTypes[formationIndex][allegiance];
  }
}